import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import Switch from '../../../../components/ui/Switch';
import { ChoicePills, ChoiceRows, SectionTitle } from './ChoiceControls';
import { GROUPINGS, LAYOUTS, SORTS, needsGrouping, type ExportGroupBy, type ExportLayout, type ExportOptions, type ExportSortBy } from './exportOptions';
import { groupByLabel, layoutHint, layoutLabel, sortByLabel } from './exportText';

interface Props {
  options: ExportOptions;
  onChange: (patch: Partial<ExportOptions>) => void;
}

const LayoutSection: React.FC<Props> = ({ options, onChange }) => {
  const { t } = useTranslation();
  const grouped = options.groupBy !== 'none';
  const perSheet = options.layout === 'oneSheetPerGroup';

  // The workbook only writes subtotal rows between groups on one sheet. Say why the switch is off rather
  // than letting it look broken: a group's own sheet always has a total, and no grouping has no subtotals.
  const subtotalsHint = perSheet
    ? t('payrollExport.subtotals.perSheetHint')
    : grouped ? t('payrollExport.subtotals.hint') : t('payrollExport.subtotals.needsGroupHint');

  return (
    <section className="space-y-5 p-5" aria-labelledby="payroll-export-layout">
      <SectionTitle id="payroll-export-layout">{t('payrollExport.section.layout')}</SectionTitle>

      <ChoiceRows<ExportLayout>
        name="payroll-export-layout"
        legend={t('payrollExport.layout.legend')}
        value={options.layout}
        choices={LAYOUTS.map((value) => ({ value, label: layoutLabel(value, t), description: layoutHint(value, t) }))}
        onChange={(layout) => onChange({ layout })}
      />

      <div>
        <ChoicePills<ExportGroupBy>
          name="payroll-export-group"
          legend={t('payrollExport.groupBy.legend')}
          value={options.groupBy}
          choices={GROUPINGS.map((value) => ({ value, label: groupByLabel(value, t) }))}
          onChange={(groupBy) => onChange({ groupBy })}
        />
        {needsGrouping(options) && (
          <p className="mt-2 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-300" data-testid="payroll-export-needs-group">
            <AlertTriangle size={14} className="mt-px shrink-0" aria-hidden="true" />
            {t('payrollExport.blocked.needsGroup')}
          </p>
        )}
        {options.layout === 'summaryAndRegister' && !grouped && (
          <p className="mt-2 text-xs text-muted-foreground">{t('payrollExport.groupBy.summaryWithoutGroupHint')}</p>
        )}
      </div>

      <ChoicePills<ExportSortBy>
        name="payroll-export-sort"
        legend={grouped ? t('payrollExport.sort.legendGrouped') : t('payrollExport.sort.legend')}
        value={options.sortBy}
        choices={SORTS.map((value) => ({ value, label: sortByLabel(value, t) }))}
        onChange={(sortBy) => onChange({ sortBy })}
      />

      <Switch
        checked={grouped && !perSheet && options.showSubtotals}
        disabled={!grouped || perSheet}
        onCheckedChange={(showSubtotals) => onChange({ showSubtotals })}
        label={t('payrollExport.subtotals.label')}
        description={subtotalsHint}
        data-testid="payroll-export-subtotals"
      />
    </section>
  );
};

export default LayoutSection;
